"use client";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import * as Command from "@/components/ui/command";
import * as Popover from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const targetLanguages = [
  {
    value: "en",
    label: "languagesMenu.en",
    flag: "https://flagcdn.com/16x12/gb.png",
  },
  {
    value: "nb",
    label: "languagesMenu.nb",
    flag: "https://flagcdn.com/16x12/no.png",
  },
  {
    value: "de",
    label: "languagesMenu.de",
    flag: "https://flagcdn.com/16x12/de.png",
  },
  {
    value: "fr",
    label: "languagesMenu.fr",
    flag: "https://flagcdn.com/16x12/fr.png",
  },
  {
    value: "se",
    label: "languagesMenu.se",
    flag: "https://flagcdn.com/16x12/se.png",
  },
  {
    value: "da",
    label: "languagesMenu.da",
    flag: "https://flagcdn.com/16x12/dk.png",
  },
  {
    value: "es",
    label: "languagesMenu.es",
    flag: "https://flagcdn.com/16x12/es.png",
  },
  {
    value: "it",
    label: "languagesMenu.it",
    flag: "https://flagcdn.com/16x12/it.png",
  },
  {
    value: "ru",
    label: "languagesMenu.ru",
    flag: "https://flagcdn.com/16x12/ru.png",
  },
  {
    value: "th",
    label: "languagesMenu.th",
    flag: "https://flagcdn.com/16x12/th.png",
  },
  {
    value: "ja",
    label: "languagesMenu.jp",
    flag: "https://flagcdn.com/16x12/jp.png",
  },
  {
    value: "zh",
    label: "languagesMenu.zh",
    flag: "https://flagcdn.com/16x12/cn.png",
  },
];

export const TargetLanguageInput = ({
  value,
  setValue,
  includeAutomatic = false,
}: {
  value: string;
  setValue: (value: string) => void;
  includeAutomatic?: boolean;
}) => {
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const _targetLanguages = includeAutomatic
    ? [{ value: "", label: "languageInput.automatic", flag: "" }, ...targetLanguages]
    : targetLanguages;

  const filteredLanguages = _targetLanguages.filter((language) => {
    return (
      t(language.label).toLowerCase().includes(searchInput.toLowerCase()) ||
      language.value.toLowerCase().includes(searchInput.toLowerCase())
    );
  });

  return (
    <Popover.Popover open={open} onOpenChange={setOpen}>
      <Popover.PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
        >
          {(function () {
            if (typeof value === "string" && (includeAutomatic || !!value)) {
              const targetLanguage = _targetLanguages.find((language) => language.value === value);
              if (targetLanguage) {
                return (
                  <div className="flex">
                    {targetLanguage.flag && (
                      <img src={targetLanguage.flag} alt="" className="mr-2 object-contain" />
                    )}
                    <span>{t(targetLanguage.label)}</span>
                  </div>
                );
              }
            }
            return t("languageInput.placeholder");
          })()}

          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </Popover.PopoverTrigger>
      <Popover.PopoverContent className="w-[200px] p-0">
        <Command.Command shouldFilter={false}>
          <Command.CommandInput
            placeholder={searchInput}
            defaultValue={searchInput}
            onValueChange={(newValue) => {
              setSearchInput(newValue);
            }}
          />
          <Command.CommandList>
            <Command.CommandEmpty>
              {t("languageInput.noLanguagesFoundMessage")}
            </Command.CommandEmpty>
            <Command.CommandGroup>
              {filteredLanguages.map((language) => (
                <Command.CommandItem
                  key={language.value}
                  value={language.value}
                  onSelect={(currentValue) => {
                    setValue(currentValue === value ? "" : currentValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === language.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {language.flag && <img src={language.flag} alt="" className="mr-2" />}
                  <span>{t(language.label)}</span>
                </Command.CommandItem>
              ))}
            </Command.CommandGroup>
          </Command.CommandList>
        </Command.Command>
      </Popover.PopoverContent>
    </Popover.Popover>
  );
};
